const BASE = '/api';

function getToken() {
  return localStorage.getItem('portfolio_token');
}

export function setToken(token) {
  localStorage.setItem('portfolio_token', token);
}

export function clearToken() {
  localStorage.removeItem('portfolio_token');
}

export function isLoggedIn() {
  return !!getToken();
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { headers, ...options });

  if (res.status === 401) {
    clearToken();
    window.location.reload();
    return;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail;
    // A structured detail ({ code, message, ... }) rides along as `error.detail`.
    const error = new Error(typeof detail === 'string' ? detail : detail?.message || `API error: ${res.status}`);
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) error.detail = detail;
    throw error;
  }
  return res.json();
}

// Unauthenticated POST that surfaces the API's error detail (used by account recovery)
async function publicPost(path, data) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : `API error: ${res.status}`);
  return body;
}

export const api = {
  // Auth
  login: async (username, password) => {
    const form = new URLSearchParams();
    form.append('username', username);
    form.append('password', password);
    const res = await fetch(`${BASE}/auth/token`, { method: 'POST', body: form });
    if (!res.ok) throw new Error('Invalid credentials');
    return res.json();
  },
  register: (data) => request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  forgotPassword: (identifier) => publicPost('/auth/forgot-password', { identifier }),
  forgotUsername: (email) => publicPost('/auth/forgot-username', { email }),
  resetPassword: (token, new_password) => publicPost('/auth/reset-password', { token, new_password }),
  getMe: () => request('/auth/me'),
  updateProfile: (data) => request('/auth/me', { method: 'PUT', body: JSON.stringify(data) }),
  updateEmail: (data) => request('/auth/me/email', { method: 'PUT', body: JSON.stringify(data) }),
  updatePassword: (data) => request('/auth/me/password', { method: 'PUT', body: JSON.stringify(data) }),

  // Accounts
  getAccounts: () => request('/accounts/'),
  createAccount: (data) => request('/accounts/', { method: 'POST', body: JSON.stringify(data) }),
  deleteAccount: (id) => request(`/accounts/${id}`, { method: 'DELETE' }),

  // Transactions
  getTransactions: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/transactions/${q ? '?' + q : ''}`);
  },
  createTransaction: (data) => request('/transactions/', { method: 'POST', body: JSON.stringify(data) }),
  updateTransaction: (id, data) => request(`/transactions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTransaction: (id) => request(`/transactions/${id}`, { method: 'DELETE' }),
  // Lots a sell can draw from; excludeSellId adds back the edited sell's own selections.
  getSellLots: (account_id, symbol, excludeSellId) => {
    const q = new URLSearchParams({ account_id, symbol, ...(excludeSellId ? { exclude_sell_id: excludeSellId } : {}) });
    return request(`/transactions/lots?${q}`);
  },

  // Cash
  getCash: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/cash/${q ? '?' + q : ''}`);
  },
  getCashBalances: () => request('/cash/balances'),
  createCash: (data) => request('/cash/', { method: 'POST', body: JSON.stringify(data) }),
  updateCash: (id, data) => request(`/cash/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCash: (id) => request(`/cash/${id}`, { method: 'DELETE' }),

  // Money market symbols (never price-fetched; history is loaded manually)
  getMoneyMarketSymbols: () => request('/money-market/symbols'),
  addMoneyMarketSymbol: (symbol) => request('/money-market/symbols', { method: 'POST', body: JSON.stringify({ symbol }) }),
  removeMoneyMarketSymbol: (symbol) => request(`/money-market/symbols/${encodeURIComponent(symbol)}`, { method: 'DELETE' }),
  getMoneyMarketRates: (symbol) => request(`/money-market/rates?symbol=${encodeURIComponent(symbol)}`),
  addMoneyMarketRate: (data) => request('/money-market/rates', { method: 'POST', body: JSON.stringify(data) }),
  updateMoneyMarketRate: (id, data) => request(`/money-market/rates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMoneyMarketRate: (id) => request(`/money-market/rates/${id}`, { method: 'DELETE' }),
  // Positions in money market symbols: yield, estimated income, accrual, missing dividends
  getMoneyMarketHoldings: () => request('/money-market/holdings'),
  dismissDividendFlag: (data) => request('/money-market/dividend-flags/dismiss', { method: 'POST', body: JSON.stringify(data) }),

  // History imports (Fidelity CSV): stage first, then load what staging flagged as new
  stageImport: (data) => request('/imports/stage', { method: 'POST', body: JSON.stringify(data) }),
  loadImport: (id) => request(`/imports/${id}/load`, { method: 'POST' }),
  discardImport: (id) => request(`/imports/${id}`, { method: 'DELETE' }),
  getImports: () => request('/imports/'),
  getImport: (id) => request(`/imports/${id}`),
  // Transaction imports: inspect (nothing stored) -> stage -> confirm sell lots -> load (loadImport)
  inspectTransactionFile: (data) => request('/imports/transactions/inspect', { method: 'POST', body: JSON.stringify(data) }),
  stageTransactionImport: (data) => request('/imports/transactions/stage', { method: 'POST', body: JSON.stringify(data) }),
  getImportLotChoices: (id, rowId) => request(`/imports/${id}/rows/${rowId}/lots`),
  setImportLots: (id, rowId, allocations) => request(`/imports/${id}/rows/${rowId}/lots`, { method: 'PUT', body: JSON.stringify({ allocations }) }),
  confirmImportLots: (id) => request(`/imports/${id}/confirm-lots`, { method: 'POST' }),

  // Positions
  getPositions: (account_id) => request(`/positions/${account_id ? '?account_id=' + account_id : ''}`),
  getBalanceHistory: (account_id) => request(`/positions/history${account_id ? '?account_id=' + account_id : ''}`),
  getClosedPositions: (account_id) => request(`/positions/closed${account_id ? '?account_id=' + account_id : ''}`),
  getPositionLots: (symbol) => request(`/positions/lots?symbol=${encodeURIComponent(symbol)}`),

  // Prices
  getDailyPrices: (symbol) => request(`/prices/daily/${symbol}`),
  getIntradayPrices: (symbol) => request(`/prices/intraday/${symbol}`),
  getLatestPrice: (symbol) => request(`/prices/latest/${symbol}`),
  refreshPrices: () => request('/prices/refresh', { method: 'POST' }),
// Alerts
  getAlerts: () => request('/alerts/'),
  createAlert: (data) => request('/alerts/', { method: 'POST', body: JSON.stringify(data) }),
  getTradeReference: ({ symbol, trade_type, account_id }) => request(
    `/alerts/trade-reference?symbol=${encodeURIComponent(symbol)}&trade_type=${trade_type}${account_id ? `&account_id=${account_id}` : ''}`
  ),
  updateAlert: (id, data) => request(`/alerts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAlert: (id) => request(`/alerts/${id}`, { method: 'DELETE' }),
  checkAlertsNow: () => request('/alerts/check-now', { method: 'POST' }),

  // Alert Events
  getAlertEvents: (unreadOnly) => request(`/alerts/events${unreadOnly ? '?unread_only=true' : ''}`),
  markEventRead: (id) => request(`/alerts/events/${id}/read`, { method: 'PUT' }),
  markAllEventsRead: () => request('/alerts/events/read-all', { method: 'PUT' }),
  deleteAlertEvent: (id) => request(`/alerts/events/${id}`, { method: 'DELETE' }),
  
  // Watchlists
  getWatchlists: () => request('/watchlists/'),
  createWatchlist: (data) => request('/watchlists/', { method: 'POST', body: JSON.stringify(data) }),
  renameWatchlist: (id, data) => request(`/watchlists/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWatchlist: (id) => request(`/watchlists/${id}`, { method: 'DELETE' }),
  getWatchlistSymbols: (id) => request(`/watchlists/${id}/symbols`),
  addSymbolToWatchlist: (id, data) => request(`/watchlists/${id}/symbols`, { method: 'POST', body: JSON.stringify(data) }),
  updateWatchlistSymbol: (id, symbol, data) => request(`/watchlists/${id}/symbols/${symbol}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeSymbolFromWatchlist: (id, symbol) => request(`/watchlists/${id}/symbols/${symbol}`, { method: 'DELETE' }),
  reorderWatchlistSymbols: (id, symbol_ids) => request(`/watchlists/${id}/reorder`, { method: 'PUT', body: JSON.stringify({ symbol_ids }) }),
  getWatchlistQuotes: (id) => request(`/watchlists/${id}/quotes`),

  // Chart settings
  getMovingAverages: () => request('/chart-settings/moving-averages'),
  setMovingAverages: (data) => request('/chart-settings/moving-averages', { method: 'PUT', body: JSON.stringify(data) }),

  // Recently viewed symbols
  getRecentlyViewed: () => request('/recently-viewed/'),
  recordSymbolView: (symbol) => request(`/recently-viewed/${symbol}`, { method: 'POST' }),
};

